import {
  buildBrand,
  alpha,
  formatDate,
  getGrade,
  getGradeColor,
  slugify,
  stripMarkdownText,
} from '../../pentest/reports/externalReportPdfHelpers';

export { alpha };

export const METHOD_ORDER = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];
export const VERIFY_THRESHOLD = 25;

export const METHOD_COLORS = {
  GET: '#0B5CAD',
  POST: '#1C7C3F',
  PUT: '#9A5B00',
  PATCH: '#7A2CB5',
  DELETE: '#C0392B',
};

export const SCORE_COLORS = {
  likely: '#C62828',
  possible: '#EF6C00',
  watchlist: '#1565C0',
  low: '#5B6B82',
};

export const RECON_METHODOLOGY_PARAGRAPHS = [
  'We begin each recon-focused web assessment by confirming the authorized scope, validating access requirements, and logging into the application when credentials or user roles are part of the review. The assessment team uses that initial access to understand the intended user journeys, privilege boundaries, and business workflows that shape how the application is supposed to behave before deeper analysis begins.',
  'With access established, we crawl the application, enumerate reachable pages, and map the functionality presented to each role or workflow. During that process, we identify the observable technology stack, supporting frameworks, client-side dependencies, and application patterns that influence likely attack paths. The goal is to build an accurate inventory of the web surface rather than relying only on the pages a user can see manually.',
  'As routes are discovered, we capture the raw HTTP requests associated with meaningful actions and normalize the methods, paths, query strings, body parameters, and route parameters involved. The assessment team then analyzes those requests to develop a threat model for each endpoint, looking at how trust boundaries are crossed, where user-controlled input is accepted, and which parts of the application may be most exposed to abuse.',
  'That threat modeling phase is used to identify attack hypotheses aligned to the kinds of weaknesses we would expect in a modern web application, including OWASP Top 10 issues, authentication and authorization weaknesses, business logic flaws, state-changing request abuse, and other implementation-specific concerns. The result is a structured recon report that helps stakeholders understand what was mapped, why certain endpoints matter, and which attack paths appear most plausible before or alongside full exploitation work.',
];

const num = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizeMethod = (value, fallback = 'GET') => {
  const normalized = String(value || fallback || 'GET').trim().toUpperCase();
  return METHOD_ORDER.includes(normalized) ? normalized : fallback;
};

const normalizePath = (value, fallback = 'Unknown endpoint') => {
  const normalized = String(value || '').trim();
  return normalized || fallback;
};

const buildEndpointKey = (path, method = '') => {
  const normalizedPath = normalizePath(path);
  const normalizedMethod = normalizeMethod(method, 'GET');
  return normalizedMethod ? `${normalizedMethod} ${normalizedPath}` : normalizedPath;
};

const severityKey = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized.includes('critical')) return 'critical';
  if (normalized.includes('high')) return 'high';
  if (normalized.includes('medium')) return 'medium';
  if (normalized.includes('low')) return 'low';
  return 'info';
};

const cleanParamToken = (value) => {
  const cleaned = String(value ?? '')
    .replace(/^['"`]+|['"`]+$/g, '')
    .trim();

  if (!cleaned) return '';
  if (cleaned === '[object Object]') return '';
  if (cleaned === '""') return '';
  if (cleaned.toLowerCase() === 'null' || cleaned.toLowerCase() === 'undefined') return '';
  return cleaned;
};

const collectParamTokens = (value) => {
  if (value == null) return [];

  if (Array.isArray(value)) {
    return value.flatMap((item) => collectParamTokens(item));
  }

  if (typeof value === 'object') {
    const preferredKeys = [
      'name',
      'key',
      'param',
      'parameter',
      'field',
      'variable',
      'query',
      'path',
      'body',
      'param_name',
      'query_param',
      'path_param',
      'body_param',
    ];
    const metadataKeys = new Set([
      'required',
      'optional',
      'description',
      'example',
      'examples',
      'type',
      'value',
      'default',
      'schema',
      'location',
      'in',
      'format',
      'nullable',
    ]);

    const entries = Object.entries(value);
    const tokens = [];
    let matchedPreferred = false;

    preferredKeys.forEach((key) => {
      if (key in value) {
        matchedPreferred = true;
        tokens.push(...collectParamTokens(value[key]));
      }
    });

    if (!matchedPreferred) {
      entries.forEach(([key, nestedValue]) => {
        const normalizedKey = cleanParamToken(key);
        if (!normalizedKey) return;
        if (!metadataKeys.has(normalizedKey.toLowerCase())) {
          tokens.push(normalizedKey);
        } else if (Array.isArray(nestedValue) || (nestedValue && typeof nestedValue === 'object')) {
          tokens.push(...collectParamTokens(nestedValue));
        }
      });
    }

    return tokens;
  }

  const raw = String(value).trim();
  if (!raw) return [];

  try {
    const parsed = JSON.parse(raw);
    if (parsed !== value) {
      return collectParamTokens(parsed);
    }
  } catch {
    // fall through
  }

  if (raw.includes(',')) {
    return raw.split(',').flatMap((item) => collectParamTokens(item));
  }

  const token = cleanParamToken(raw);
  return token ? [token] : [];
};

const parseParamList = (value) => [...new Set(collectParamTokens(value))];

const stripQueryString = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';

  try {
    if (raw.includes('://')) {
      const parsed = new URL(raw);
      return `${parsed.pathname || '/'}${parsed.hash || ''}` || raw;
    }
  } catch {
    // ignore parse errors
  }

  return raw.split('?')[0];
};

const extractQueryKeysFromPath = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return [];

  try {
    if (raw.includes('://')) {
      const parsed = new URL(raw);
      return [...new Set(Array.from(parsed.searchParams.keys()).map((item) => item.trim()).filter(Boolean))];
    }
  } catch {
    // ignore parse errors
  }

  const queryIndex = raw.indexOf('?');
  if (queryIndex === -1) return [];

  return [
    ...new Set(
      Array.from(new URLSearchParams(raw.slice(queryIndex + 1)).keys())
        .map((item) => item.trim())
        .filter(Boolean)
    ),
  ];
};

const extractRouteParamKeys = (value) => {
  const raw = stripQueryString(value);
  if (!raw) return [];

  const matches = [
    ...Array.from(raw.matchAll(/:([A-Za-z0-9_-]+)/g)).map((match) => match[1]),
    ...Array.from(raw.matchAll(/\{([A-Za-z0-9_-]+)\}/g)).map((match) => match[1]),
    ...Array.from(raw.matchAll(/<([A-Za-z0-9_-]+)>/g)).map((match) => match[1]),
  ];

  return [...new Set(matches.map((item) => item.trim()).filter(Boolean))];
};

const parseLocation = (value) => {
  if (!value) return { path: '', method: '' };

  if (Array.isArray(value)) {
    return parseLocation(value.find(Boolean) || '');
  }

  const raw = String(value).trim();
  if (!raw) return { path: '', method: '' };

  if (raw.startsWith('[') && raw.endsWith(']')) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parseLocation(parsed.find(Boolean) || '');
    } catch {
      // ignore parse errors
    }
  }

  const parts = raw.split(' - ');
  if (parts.length >= 2) {
    const method = normalizeMethod(parts[parts.length - 1], '');
    if (METHOD_ORDER.includes(method)) {
      return {
        path: normalizePath(parts.slice(0, -1).join(' - ')),
        method,
      };
    }
  }

  const matched = raw.match(/^(.*)\s+-\s+([A-Z]+)$/i);
  if (matched) {
    const method = normalizeMethod(matched[2], '');
    if (METHOD_ORDER.includes(method)) {
      return {
        path: normalizePath(matched[1]),
        method,
      };
    }
  }

  return { path: normalizePath(raw), method: '' };
};

const compactPath = (value, maxLength = 84) => {
  const cleaned = String(value || '').trim();
  if (!cleaned) return '';
  return cleaned.length > maxLength ? `${cleaned.slice(0, maxLength - 3).trim()}...` : cleaned;
};

const softWrapEndpointText = (value) =>
  String(value || '').replace(/([/_?&=.-])/g, '$1\u200b');

const cleanParagraphs = (value, fallback = []) => {
  const paragraphs = String(value || '')
    .split(/\n{2,}/g)
    .map((paragraph) => stripMarkdownText(paragraph))
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);

  return paragraphs.length ? paragraphs : fallback;
};

export const scoreLabel = (score) => {
  const numeric = num(score);
  if (numeric >= 75) return 'Likely';
  if (numeric >= 50) return 'Possible';
  if (numeric >= VERIFY_THRESHOLD) return 'Watchlist';
  return 'Low';
};

export const scoreColor = (score) => {
  const label = scoreLabel(score).toLowerCase();
  return SCORE_COLORS[label] || SCORE_COLORS.low;
};

const buildExecutiveSummary = ({
  totalEndpoints,
  activeMethodCount,
  endpointsWithParams,
  stateChangingRoutes,
  endpointsWithLikelyPaths,
  totalHypotheses,
  totalLikelyAttacks,
  endpointsWithSummaries,
  findingsPresent,
  topRiskEndpoint,
}) => {
  const summaryCoverage = totalEndpoints ? Math.round((endpointsWithSummaries / totalEndpoints) * 100) : 0;
  const paramCoverage = totalEndpoints ? Math.round((endpointsWithParams / totalEndpoints) * 100) : 0;

  const paragraphOne = [
    `The assessment team mapped ${totalEndpoints} web endpoint${totalEndpoints === 1 ? '' : 's'} across ${activeMethodCount} observed HTTP method${activeMethodCount === 1 ? '' : 's'}.`,
    stateChangingRoutes
      ? `${stateChangingRoutes} state-changing route${stateChangingRoutes === 1 ? '' : 's'} were identified for deeper review because they can materially affect application behavior and trust boundaries.`
      : 'The inventoried routes were primarily read-oriented, which narrows the set of state-changing behaviors that require follow-up testing.',
  ].join(' ');

  const paragraphTwo = [
    `${endpointsWithParams} endpoint${endpointsWithParams === 1 ? '' : 's'} exposed identifiable input surfaces, representing ${paramCoverage}% of the inventoried application map.`,
    `${summaryCoverage}% of the endpoints included usable functional summaries, which helped the assessment team tie raw routes back to business workflows and prioritize review around meaningful actions instead of isolated URLs alone.`,
  ].join(' ');

  const paragraphThree = [
    `Threat modeling produced ${totalHypotheses} attack hypothesis${totalHypotheses === 1 ? '' : 'es'} in total, with ${totalLikelyAttacks} scoring at or above the active verification threshold and spread across ${endpointsWithLikelyPaths} endpoint${endpointsWithLikelyPaths === 1 ? '' : 's'}.`,
    topRiskEndpoint
      ? `The most exposed route in the current dataset was ${topRiskEndpoint.method} ${topRiskEndpoint.displayPath}, which combined the highest threat-model score with the richest surrounding context for follow-up validation.`
      : 'No single endpoint materially stood out above the rest of the current dataset, suggesting a flatter recon profile across the mapped routes.',
  ].join(' ');

  const paragraphFour = findingsPresent
    ? `${findingsPresent} confirmed finding${findingsPresent === 1 ? '' : 's'} were already associated to the mapped routes elsewhere in the engagement data. This recon report remains focused on endpoint coverage and plausible attack paths so stakeholders can understand how the application was mapped and where additional validation effort should be directed next.`
    : 'This recon report is intentionally centered on application mapping and attack-path development rather than confirmed exploitation. Its value is in showing which routes were inventoried, how the application behaves, and where the assessment team would concentrate validation effort next.';

  return [paragraphOne, paragraphTwo, paragraphThree, paragraphFour];
};

export const buildWebReconReportPdfData = ({
  pentestId,
  primaryColor,
  companyName,
  clientName,
  stats,
  endpoints,
  threatModel,
  exploits,
}) => {
  const brand = buildBrand(primaryColor || '#0A68FF');
  const endpointInventory = Array.isArray(endpoints) ? endpoints : endpoints?.endpoints || [];
  const threatEndpoints = Array.isArray(threatModel)
    ? threatModel
    : threatModel?.endpoints || threatModel?.threat_model?.endpoints || [];
  const findings = Array.isArray(exploits) ? exploits : exploits?.exploits || [];

  const map = new Map();

  const ensureEntry = (path, method, seed = {}) => {
    const resolvedPath = normalizePath(path, `Endpoint ${map.size + 1}`);
    const resolvedMethod = normalizeMethod(method, 'GET');
    const key = buildEndpointKey(resolvedPath, resolvedMethod);

    if (!map.has(key)) {
      map.set(key, {
        key,
        path: resolvedPath,
        method: resolvedMethod,
        domain: '',
        summary: '',
        bodyParams: [],
        queryParams: [],
        pathParams: [],
        inventoryCounts: { critical: 0, high: 0, medium: 0, low: 0, info: 0 },
        inventoryTotalFindings: 0,
        matchedFindings: [],
        matchedAttacks: [],
        threatNarrative: '',
        sourceIds: [],
        ...seed,
      });
    }

    return map.get(key);
  };

  endpointInventory.forEach((endpoint, index) => {
    const method = normalizeMethod(endpoint?.request_type, 'GET');
    const path = normalizePath(endpoint?.path || endpoint?.endpoint || endpoint?.url, `Endpoint ${index + 1}`);
    const entry = ensureEntry(path, method);

    entry.domain = endpoint?.domain || entry.domain;
    entry.summary = endpoint?.summary || entry.summary;
    const nextBody = parseParamList(endpoint?.body_param);
    const nextQuery = parseParamList(endpoint?.query_param);
    const nextPath = parseParamList(endpoint?.path_param);

    entry.bodyParams = nextBody.length ? nextBody : entry.bodyParams;
    entry.queryParams = nextQuery.length ? nextQuery : entry.queryParams;
    entry.pathParams = nextPath.length ? nextPath : entry.pathParams;
    entry.inventoryTotalFindings = Math.max(entry.inventoryTotalFindings, num(endpoint?.total_exploits_found, 0));
    entry.inventoryCounts = {
      critical: Math.max(entry.inventoryCounts.critical, num(endpoint?.critical_exploits_found, 0)),
      high: Math.max(entry.inventoryCounts.high, num(endpoint?.high_exploits_found, 0)),
      medium: Math.max(entry.inventoryCounts.medium, num(endpoint?.medium_exploits_found, 0)),
      low: Math.max(entry.inventoryCounts.low, num(endpoint?.low_exploits_found, 0)),
      info: Math.max(entry.inventoryCounts.info, num(endpoint?.info_exploits_found, 0)),
    };

    if (endpoint?.id != null) entry.sourceIds.push(String(endpoint.id));
  });

  threatEndpoints.forEach((endpoint) => {
    const method = normalizeMethod(endpoint?.http_method, 'GET');
    const path = normalizePath(endpoint?.path);
    const entry = ensureEntry(path, method);

    const attacks = Array.isArray(endpoint?.threat_model?.attacks) ? endpoint.threat_model.attacks : [];
    entry.matchedAttacks = [...attacks].sort((left, right) => num(right?.score, 0) - num(left?.score, 0));
    entry.threatNarrative = endpoint?.threat_model?.threat_model || entry.threatNarrative;
  });

  findings.forEach((finding, index) => {
    const location = parseLocation(
      finding?.impacted_assets || finding?.endpoint || finding?.asset || finding?.location || ''
    );

    let entry;
    if (location.path && location.method) {
      entry = ensureEntry(location.path, location.method);
    } else if (location.path) {
      entry = Array.from(map.values()).find((row) => row.path === location.path) || ensureEntry(location.path, 'GET');
    } else {
      entry = ensureEntry(`Endpoint Finding ${index + 1}`, 'GET');
    }

    entry.matchedFindings.push(finding);
  });

  const endpointRows = Array.from(map.values())
    .map((row) => {
      const matchedSeverityCounts = { critical: 0, high: 0, medium: 0, low: 0, info: 0 };
      row.matchedFindings.forEach((finding) => {
        matchedSeverityCounts[severityKey(finding?.severity)] += 1;
      });

      const severityCounts = {
        critical: Math.max(row.inventoryCounts.critical, matchedSeverityCounts.critical),
        high: Math.max(row.inventoryCounts.high, matchedSeverityCounts.high),
        medium: Math.max(row.inventoryCounts.medium, matchedSeverityCounts.medium),
        low: Math.max(row.inventoryCounts.low, matchedSeverityCounts.low),
        info: Math.max(row.inventoryCounts.info, matchedSeverityCounts.info),
      };

      const severityTotal = Object.values(severityCounts).reduce((sum, value) => sum + value, 0);
      const totalFindings = Math.max(row.inventoryTotalFindings, row.matchedFindings.length, severityTotal);
      const inlineQueryParams = extractQueryKeysFromPath(row.path);
      const inlinePathParams = extractRouteParamKeys(row.path);
      const bodyParams = [...new Set(row.bodyParams)];
      const queryParams = [...new Set([...row.queryParams, ...inlineQueryParams])];
      const pathParams = [...new Set([...row.pathParams, ...inlinePathParams])];
      const displayPath = stripQueryString(row.path) || row.path;
      const paramPreview = [...pathParams, ...queryParams, ...bodyParams];
      const paramTotal = bodyParams.length + queryParams.length + pathParams.length;
      const likelyAttacks = row.matchedAttacks.filter((attack) => num(attack?.score, 0) >= VERIFY_THRESHOLD);
      const topAttackScore = row.matchedAttacks.reduce((max, attack) => Math.max(max, num(attack?.score, 0)), 0);
      const summary = String(row.summary || '').trim();
      const narrative = stripMarkdownText(row.threatNarrative || '').trim();
      const hasSummary =
        Boolean(summary) &&
        summary !== '_No summary available for this endpoint._' &&
        summary.toLowerCase() !== 'no summary available for this endpoint.';
      const stateChanging = ['POST', 'PUT', 'DELETE', 'PATCH'].includes(row.method);
      const summaryText = hasSummary ? stripMarkdownText(summary) : narrative;
      const riskIndex =
        totalFindings * 14 +
        likelyAttacks.length * 5 +
        topAttackScore * 0.25 +
        paramTotal * 0.8 +
        (stateChanging ? 4 : 0) +
        (summaryText ? 1 : 0);

      const attacks = row.matchedAttacks.map((attack, attackIndex) => ({
        id: `${slugify(`${displayPath}-${attack?.name || `attack-${attackIndex + 1}`}`)}-${attackIndex + 1}`,
        name: stripMarkdownText(attack?.name || `Attack ${attackIndex + 1}`),
        score: num(attack?.score, 0),
        label: scoreLabel(attack?.score),
        color: scoreColor(attack?.score),
        verified: num(attack?.score, 0) >= VERIFY_THRESHOLD,
        description: cleanParagraphs(
          attack?.reason || attack?.description || attack?.details || attack?.rationale || '',
          ['No supporting attack narrative was provided for this hypothesis.']
        ),
      }));

      return {
        ...row,
        displayPath,
        compactPath: compactPath(displayPath || row.path),
        title: `${row.method} ${displayPath || row.path}`,
        wrappedPath: softWrapEndpointText(row.path),
        wrappedDisplayPath: softWrapEndpointText(displayPath || row.path),
        wrappedTitle: softWrapEndpointText(`${row.method} ${displayPath || row.path}`),
        bodyParams,
        queryParams,
        pathParams,
        paramPreview,
        paramTotal,
        likelyAttacks: attacks.filter((attack) => attack.verified),
        likelyAttackCount: likelyAttacks.length,
        totalAttackCount: attacks.length,
        topAttackScore,
        severityCounts,
        totalFindings,
        hasSummary: Boolean(summaryText),
        summaryText: summaryText || 'No functional summary was captured for this endpoint during the current recon pass.',
        stateChanging,
        riskIndex,
        attacks,
      };
    })
    .sort((left, right) => {
      if (right.topAttackScore !== left.topAttackScore) return right.topAttackScore - left.topAttackScore;
      if (right.likelyAttackCount !== left.likelyAttackCount) return right.likelyAttackCount - left.likelyAttackCount;
      if (right.riskIndex !== left.riskIndex) return right.riskIndex - left.riskIndex;
      return left.title.localeCompare(right.title);
    })
    .map((row, index) => ({
      ...row,
      ordinal: index + 1,
      id: `endpoint-${index + 1}-${slugify(row.title)}`,
    }));

  const totalEndpoints = endpointRows.length;
  const methodCounts = METHOD_ORDER.reduce((accumulator, label) => ({
    ...accumulator,
    [label]: endpointRows.filter((row) => row.method === label).length,
  }), {});
  const activeMethodCount = METHOD_ORDER.filter((method) => num(methodCounts[method], 0) > 0).length;
  const endpointsWithFindings = endpointRows.filter((row) => row.totalFindings > 0).length;
  const endpointsWithParams = endpointRows.filter((row) => row.paramTotal > 0).length;
  const stateChangingRoutes = endpointRows.filter((row) => row.stateChanging).length;
  const endpointsWithLikelyPaths = endpointRows.filter((row) => row.likelyAttackCount > 0).length;
  const endpointsWithSummaries = endpointRows.filter((row) => row.hasSummary).length;
  const totalHypotheses = endpointRows.reduce((sum, row) => sum + row.totalAttackCount, 0);
  const totalLikelyAttacks = endpointRows.reduce((sum, row) => sum + row.likelyAttackCount, 0);
  const findingsPresent = endpointRows.reduce((sum, row) => sum + row.totalFindings, 0);
  const averageTopScore = totalEndpoints
    ? Math.round(endpointRows.reduce((sum, row) => sum + row.topAttackScore, 0) / totalEndpoints)
    : 0;
  const topRiskEndpoint = endpointRows[0] || null;
  const reportDate = formatDate(stats?.meta?.generated_at || Date.now());
  const riskScore = stats?.risk_score ?? stats?.score ?? null;
  const grade = getGrade(riskScore);

  const topEndpoints = endpointRows.slice(0, 5).map((row) => ({
    id: row.id,
    title: row.title,
    wrappedTitle: row.wrappedTitle,
    score: row.topAttackScore,
    likelyAttackCount: row.likelyAttackCount,
    totalAttackCount: row.totalAttackCount,
  }));

  const executiveSummary = buildExecutiveSummary({
    totalEndpoints,
    activeMethodCount,
    endpointsWithParams,
    stateChangingRoutes,
    endpointsWithLikelyPaths,
    totalHypotheses,
    totalLikelyAttacks,
    endpointsWithSummaries,
    findingsPresent,
    topRiskEndpoint,
  });

  const tocItems = [
    { id: 'cover', title: 'Cover', level: 1 },
    { id: 'confidentiality', title: 'Confidentiality', level: 1 },
    { id: 'table-of-contents', title: 'Table of Contents', level: 1 },
    { id: 'executive-summary', title: 'Executive Summary', level: 1 },
    { id: 'methodology', title: 'Methodology', level: 1 },
    ...endpointRows.map((row) => ({
      id: row.id,
      title: `Endpoint ${row.ordinal}: ${row.method} ${compactPath(row.displayPath, 52)}`,
      level: 1,
    })),
  ];

  return {
    pentestId,
    clientName: clientName || stats?.meta?.client_name || stats?.client || 'Client Name',
    companyName: companyName || 'StealthNet AI',
    reportDate,
    testingType: 'Web Reconnaissance & Threat Modeling',
    organizationName: stats?.meta?.client_name || stats?.client || clientName || 'Client',
    brand,
    cover: {
      eyebrow: 'Recon Report',
      title: 'Web Application Recon Report',
      subtitle: 'Endpoint Inventory and Threat Modeling',
      tags: ['Reconnaissance', 'Threat Modeling', 'Endpoint Analysis'],
    },
    grade,
    gradeColor: getGradeColor(grade),
    riskScore,
    executiveSummary,
    methodology: RECON_METHODOLOGY_PARAGRAPHS,
    endpointRows,
    tocItems,
    metrics: {
      totalEndpoints,
      activeMethodCount,
      endpointsWithParams,
      stateChangingRoutes,
      endpointsWithLikelyPaths,
      endpointsWithSummaries,
      endpointsWithFindings,
      findingsPresent,
      totalHypotheses,
      totalLikelyAttacks,
      averageTopScore,
    },
    methodCounts,
    topEndpoints,
  };
};
